import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '@libs/contexts/TenantContext';
import { useAssistant } from './AssistantContext';
import {
  createAiConversation,
  sendAiMessage,
  sendAssistantFeedback,
  updateAiConversation,
} from './assistantApi';
import AssistantSupportCard from './AssistantSupportCard';
import {
  ASK_PLACEHOLDER,
  ASSISTANT_DOCK_DEFAULT,
  apiPins,
  describeAssistantContext,
  inferAssistantMode,
} from './assistantLayout';

const NOTES_KEY = 'signalbridge.assistant.operatorNotes';
const NOTES_MAX = 2000;

function loadNotes() {
  try {
    return localStorage.getItem(NOTES_KEY) || '';
  } catch {
    return '';
  }
}

function tenantIdFrom(userData, userClaims, selectedTenant) {
  return (
    selectedTenant?.id ||
    userData?.tenantId ||
    userClaims?.tenantId ||
    userClaims?.tenant_id ||
    ''
  );
}

export default function AssistantDrawer() {
  const {
    isOpen,
    closeAssistant,
    mode,
    pins,
    clearPins,
    seedMessage,
    status,
    dockWidth,
    setDockWidth,
    isDesktop,
  } = useAssistant();
  const auth = useAuth() || {};
  const { selectedTenant } = useTenant();
  const tenantId = tenantIdFrom(auth.userData, auth.userClaims, selectedTenant);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toolNote, setToolNote] = useState('');
  const [notes, setNotes] = useState(loadNotes);
  const [notesOpen, setNotesOpen] = useState(false);
  const scroller = useRef(null);
  const inputRef = useRef(null);
  const lastPatchedPins = useRef('');
  const dragRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAssistant();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeAssistant]);

  useEffect(() => {
    setConversationId(null);
    setMessages([]);
    setError('');
    lastPatchedPins.current = '';
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;
    setInput(seedMessage || '');
  }, [isOpen, seedMessage]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(NOTES_KEY, notes);
    } catch {
      /* ignore quota / private mode */
    }
  }, [notes]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, toolNote]);

  useEffect(() => {
    if (!conversationId || !tenantId) return;
    const serialized = JSON.stringify(apiPins(pins));
    if (lastPatchedPins.current === serialized) return;
    lastPatchedPins.current = serialized;
    updateAiConversation({ conversationId, tenantId, pins: apiPins(pins) }).catch(() => {});
  }, [conversationId, tenantId, pins]);

  if (status?.enabled !== true) return null;

  const canSetup = status?.canSetup === true;
  const canTroubleshoot = status?.canTroubleshoot === true;
  const context = describeAssistantContext(pins);
  const panelWidth = isDesktop ? dockWidth || ASSISTANT_DOCK_DEFAULT : undefined;
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setError('');
    setToolNote('Working on this…');
    setInput('');
    setMessages((m) => [...m, { role: 'user', text }]);
    try {
      let cid = conversationId;
      if (!cid) {
        const convoMode = inferAssistantMode(text, { canSetup, canTroubleshoot });
        const convo = await createAiConversation({
          mode: convoMode,
          pins: apiPins(pins),
          tenantId,
          operatorNotes: notes,
          pageContext: { route: window.location.pathname },
        });
        cid = convo.conversationId || convo.id;
        lastPatchedPins.current = JSON.stringify(apiPins(pins));
        setConversationId(cid);
      }
      const result = await sendAiMessage({
        conversationId: cid,
        message: text,
        tenantId,
        operatorNotes: notes,
        onEvent: (event, data) => {
          if (event === 'status') setToolNote('Working on this…');
          if (event === 'tool_start') setToolNote(`Looking up ${data?.name || 'data'}…`);
          if (event === 'tool_end') setToolNote('Working on this…');
        },
      });
      setMessages((m) => [...m, { role: 'assistant', payload: result }]);
    } catch (err) {
      setError(err.message || 'Assistant unavailable');
    } finally {
      setBusy(false);
      setToolNote('');
    }
  };

  const onResizePointerDown = (event) => {
    if (!isDesktop) return;
    event.preventDefault();
    dragRef.current = {
      startX: event.clientX,
      startWidth: dockWidth || ASSISTANT_DOCK_DEFAULT,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* jsdom may not implement pointer capture */
    }
  };

  const onResizePointerMove = (event) => {
    if (!dragRef.current) return;
    setDockWidth(dragRef.current.startWidth + (dragRef.current.startX - event.clientX));
  };

  const onResizePointerUp = (event) => {
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* jsdom may not implement pointer capture */
    }
  };

  const panel = (
    <aside
      data-testid="assistant-panel"
      data-assistant-layout={isDesktop ? 'docked' : 'overlay'}
      role={isDesktop ? 'complementary' : 'dialog'}
      aria-modal={isDesktop ? undefined : 'true'}
      aria-labelledby="assistant-title"
      hidden={!isOpen}
      className={
        isDesktop
          ? `fixed inset-y-0 right-0 z-[45] bg-white border-l border-gray-200 shadow-sm ${isOpen ? 'flex flex-col' : 'hidden'}`
          : `absolute inset-y-0 right-0 w-full bg-white shadow-lg ${isOpen ? 'flex flex-col' : 'hidden'}`
      }
      style={isDesktop ? { width: panelWidth } : undefined}
    >
      {isDesktop ? (
        <button
          type="button"
          data-testid="assistant-resize"
          aria-label="Resize assistant"
          className="absolute inset-y-0 left-0 w-1.5 cursor-col-resize border-0 bg-transparent hover:bg-blue-200/70 p-0"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
        />
      ) : null}
      <header className="px-3 py-2 border-b border-gray-200 bg-slate-50">
        <div className="flex items-center justify-between gap-2">
          <h2 id="assistant-title" className="text-sm font-semibold text-gray-900">
            Signal Bridge Assistant
          </h2>
          <button type="button" onClick={closeAssistant} className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-200" aria-label="Close assistant">
            ×
          </button>
        </div>
        {context ? (
          <div className="mt-2 flex items-start justify-between gap-2">
            <p data-testid="assistant-context" className="text-xs text-gray-600">
              Context: {context.name} — {context.type}
            </p>
            <button type="button" onClick={clearPins} className="shrink-0 text-xs text-blue-700 hover:underline">
              Clear context
            </button>
          </div>
        ) : (
          <p data-testid="assistant-context" className="mt-2 text-xs text-gray-500">No object in context.</p>
        )}
      </header>

      <div ref={scroller} className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {messages.map((msg, idx) => (
          <MessageBubble
            key={idx}
            msg={msg}
            conversationId={conversationId}
            tenantId={tenantId}
            messageId={msg.role === 'assistant' ? `assistant-${idx}` : undefined}
            onFeedback={async (rating, extra = {}) => sendAssistantFeedback({ conversationId, tenantId, rating, ...extra })}
            onTryAgain={() => inputRef.current?.focus()}
          />
        ))}
        {toolNote ? <p data-testid="assistant-working" className="text-xs text-gray-500">{toolNote}</p> : null}
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
      </div>

      <footer className="px-3 py-2 border-t border-gray-200 bg-slate-50 space-y-2">
        <button
          type="button"
          className="text-xs text-gray-600 hover:text-gray-900"
          onClick={() => setNotesOpen((open) => !open)}
          aria-expanded={notesOpen}
        >
          Instructions{notes.trim() ? ' (on)' : ''}
        </button>
        {notesOpen ? (
          <div>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={3}
              maxLength={NOTES_MAX}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
              placeholder="Already-known issues, what to look at next, how you want answers…"
            />
            <p className="mt-1 text-xs text-gray-500">
              Steers this chat, like ChatGPT custom instructions. Cannot override product rules.
            </p>
          </div>
        ) : null}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex gap-2 items-end"
        >
          <textarea
            ref={inputRef}
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[44px] max-h-32 resize-y break-words"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={ASK_PLACEHOLDER}
            disabled={busy}
          />
          <button type="submit" className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md disabled:opacity-50" disabled={busy}>
            Send
          </button>
        </form>
      </footer>
    </aside>
  );

  if (isDesktop) return panel;

  return (
    <div className={`fixed inset-0 z-[60] ${isOpen ? '' : 'hidden'}`} hidden={!isOpen} data-testid="assistant-overlay">
      <button
        type="button"
        data-testid="assistant-backdrop"
        className="absolute inset-0 bg-black/30 border-0 cursor-default"
        aria-label="Dismiss assistant overlay"
        onClick={closeAssistant}
      />
      {panel}
    </div>
  );
}

function MessageBubble({ msg, conversationId, messageId, onFeedback, onTryAgain }) {
  if (msg.role === 'user') {
    return (
      <div className="bg-blue-50 border border-blue-100 rounded-md px-3 py-2 text-sm text-gray-900 whitespace-pre-wrap break-words">
        {msg.text}
      </div>
    );
  }
  const p = msg.payload || {};
  const proposal = p.proposal || (p.kind === 'setup' ? p : null);
  const isTs = p.kind === 'troubleshooting' || p.whatHappened || p.fix || (p.issues || []).length;
  return (
    <div className="border border-gray-200 rounded-md px-3 py-2 text-sm space-y-2">
      {p.summary && p.kind === 'setup' ? <p className="font-medium">{p.summary}</p> : null}
      {isTs && p.kind !== 'setup' ? <TroubleshootCard payload={p} /> : null}
      {proposal && (proposal.entitiesReferenced || proposal.entitiesToCreate) ? (
        <ProposalCard proposal={proposal} />
      ) : null}
      {typeof p === 'string' ? <p>{p}</p> : null}
      {p && typeof p === 'object' ? (
        <AssistantSupportCard
          conversationId={conversationId}
          messageId={messageId}
          onFeedback={onFeedback}
          onTryAgain={onTryAgain}
        />
      ) : null}
    </div>
  );
}

const CONFIDENCE_LABEL = {
  confirmed: 'Sure',
  strongly_supported: 'Strong evidence',
  probable: 'Likely',
  insufficient_evidence: 'Not enough proof',
};

function confidenceLabel(value) {
  return CONFIDENCE_LABEL[value] || value;
}

function looksLikeToolRecipe(text) {
  if (!text) return false;
  return /\b(get_flow_run|find_flow_runs|get_flow|find_flows|get_action|get_trigger)\b|you can do this by running|with runId `/i.test(
    String(text)
  );
}

function TroubleshootCard({ payload }) {
  const issues = Array.isArray(payload.issues)
    ? payload.issues.filter((row) => row && (row.fix || row.title) && !looksLikeToolRecipe(row.fix) && !looksLikeToolRecipe(row.title))
    : [];
  const rawFix = payload.fix || payload.recommendedNextAction || issues[0]?.fix;
  const discardedRecipe = looksLikeToolRecipe(rawFix);
  const primaryFix = discardedRecipe ? issues[0]?.fix : rawFix;
  const primaryWhy = looksLikeToolRecipe(issues[0]?.why)
    ? payload.whatHappened || payload.likelyCause
    : issues[0]?.why || payload.whatHappened || payload.likelyCause;
  const confidence = discardedRecipe && !primaryFix
    ? null
    : issues[0]?.confidence || payload.confidence;
  const extra = issues.slice(primaryFix && issues[0]?.fix === primaryFix ? 1 : 0).filter((row) => row.fix !== primaryFix);
  const evidence = Array.isArray(payload.evidence)
    ? payload.evidence.filter((row) => row && (row.fact || row.ref))
    : [];
  const proofEndsAt = payload.proofEndsAt || payload.proof_ends_at;
  return (
    <div className="space-y-3">
      {primaryFix ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Do this</p>
          <p className="text-gray-900 whitespace-pre-wrap break-words">{primaryFix}</p>
        </div>
      ) : null}
      {primaryWhy ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What was wrong</p>
          <p className="text-gray-900 whitespace-pre-wrap break-words">{primaryWhy}</p>
          {confidence ? (
            <p className="mt-1 text-xs text-gray-600">Confidence: {confidenceLabel(confidence)}</p>
          ) : null}
        </div>
      ) : null}
      {extra.map((issue, index) => (
        <div key={index} className="border-t border-gray-100 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Also check</p>
          <p className="text-gray-900">{issue.fix || issue.title}</p>
          {issue.why ? <p className="text-xs text-gray-600 mt-1">{issue.why}</p> : null}
          {issue.confidence ? (
            <p className="mt-1 text-xs text-gray-600">Confidence: {confidenceLabel(issue.confidence)}</p>
          ) : null}
        </div>
      ))}
      {evidence.length ? (
        <div className="border-t border-gray-100 pt-2 space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Evidence</p>
          {evidence.slice(0, 4).map((row, index) => (
            <p key={index} className="text-xs text-gray-700">
              {row.source ? `${row.source}: ` : ''}
              {row.fact || row.ref}
            </p>
          ))}
        </div>
      ) : null}
      {proofEndsAt ? (
        <p className="text-xs text-gray-600">
          <span className="font-semibold uppercase tracking-wide text-gray-500">Proof ends at. </span>
          {proofEndsAt}
        </p>
      ) : (
        <p className="text-xs text-gray-500">Proof ends at: not stated. Treat success as unproven unless evidence says otherwise.</p>
      )}
    </div>
  );
}

function ProposalCard({ proposal }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded p-2">
      <p className="text-xs uppercase tracking-wide text-gray-500">Proposed configuration</p>
      {proposal.summary ? <p className="mt-1">{proposal.summary}</p> : null}
      <IdList title="Reuse" items={proposal.entitiesReferenced} />
      <IdList title="Create" items={proposal.entitiesToCreate} />
      <IdList title="Modify" items={proposal.entitiesToModify} />
      {(proposal.warnings || []).map((w, i) => (
        <p key={i} className="text-xs text-amber-800 mt-1">{w.message || w.code || String(w)}</p>
      ))}
      {(proposal.unresolvedInputs || []).map((u, i) => (
        <p key={i} className="text-xs text-gray-700 mt-1">Need: {u.question || u.field}</p>
      ))}
    </div>
  );
}

function IdList({ title, items }) {
  if (!items || !items.length) return null;
  return (
    <p className="text-xs mt-1">
      <span className="font-medium">{title}:</span>{' '}
      {items.map((it) => it.name || it.id || it.tempId || it.kind).join(', ')}
    </p>
  );
}
